import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Loading from '../components/Loading';
import { useGetDiceRoundBetsQuery } from '../services/dice/diceServices';

const formatDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
};

const LudoAllBetsPage = () => {
  const navigate = useNavigate();
  const { roundId } = useParams();
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  const { data: betsData, isLoading, error } = useGetDiceRoundBetsQuery({ roundId }, { skip: !roundId });

  const bets = betsData?.data || [];
  const summary = betsData?.summary || {};
  const paginated = bets.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.ceil(bets.length / PAGE_SIZE);

  return (
    <div className="w-full">
      <div className="flex justify-center">
        <div className="w-full px-2">
          <div className="bg-white rounded shadow border border-gray-200">
            <div className="bg-[#3e525f] px-5 py-3 flex items-center justify-between">
              <span className="text-xl text-white font-normal">Ludo Bets — Round {roundId}</span>
              <button onClick={() => navigate(-1)} className="bg-white text-gray-700 px-4 py-1.5 hover:bg-gray-100 transition-colors">
                Back
              </button>
            </div>

            {/* Summary */}
            <div className="flex flex-wrap gap-4 px-5 py-3 bg-gray-50 border-b border-gray-200 text-sm">
              {[
                { label: 'Total Bets', value: summary.totalBets || bets.length, cls: 'text-gray-800' },
                { label: 'Won', value: summary.wonBets || 0, cls: 'text-green-600' },
                { label: 'Lost', value: summary.lostBets || 0, cls: 'text-red-500' },
                { label: 'P/L', value: (summary.pl || 0).toFixed(2), cls: (summary.pl || 0) >= 0 ? 'text-green-600' : 'text-red-500' },
              ].map(s => (
                <div key={s.label} className="bg-white border border-gray-200 rounded px-4 py-2 min-w-[110px]">
                  <div className="text-xs text-gray-400 mb-0.5">{s.label}</div>
                  <div className={`text-lg font-bold ${s.cls}`}>{s.value}</div>
                </div>
              ))}
            </div>

            <div className="overflow-x-auto">
              {isLoading ? (
                <div className="flex justify-center py-8"><Loading /></div>
              ) : error ? (
                <div className="p-4 text-red-500 text-center">{error?.message || 'Error loading bets'}</div>
              ) : (
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-[#3e525f] text-white">
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">#</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">Username</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">Bet On</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">Amount</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">Status</th>
                      <th className="py-3 px-4 text-left font-medium">Place Time</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {paginated.length === 0 ? (
                      <tr><td colSpan="6" className="p-8 text-center text-gray-400">No bets found</td></tr>
                    ) : (
                      paginated.map((bet, idx) => (
                        <tr key={bet._id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="py-2.5 px-4 border-r border-gray-200">{(page - 1) * PAGE_SIZE + idx + 1}</td>
                          <td className="py-2.5 px-4 border-r border-gray-200">
                            <div className="font-semibold text-gray-800">{bet.fullName || bet.user?.fullName || '-'}</div>
                            {bet.userId && <div className="text-xs text-blue-500">{bet.userId}</div>}
                          </td>
                          <td className="py-2.5 px-4 border-r border-gray-200">{bet.betOn || bet.selection || '-'}</td>
                          <td className="py-2.5 px-4 border-r border-gray-200">{bet.stake ?? bet.amount ?? '-'}</td>
                          <td className="py-2.5 px-4 border-r border-gray-200">
                            <span className={`px-2 py-0.5 text-xs rounded font-medium capitalize ${
                              bet.status === 'won' ? 'bg-green-100 text-green-700'
                              : bet.status === 'lost' ? 'bg-red-100 text-red-700'
                              : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {bet.status || 'pending'}
                            </span>
                          </td>
                          <td className="py-2.5 px-4 text-gray-600">{formatDate(bet.placedAt || bet.createdAt)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {totalPages > 1 && (
              <div className="flex justify-end gap-1 px-4 py-3 border-t border-gray-200">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 border border-gray-300 text-sm disabled:opacity-40">‹</button>
                <span className="px-3 py-1 bg-[#3e525f] text-white text-sm">{page} / {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1 border border-gray-300 text-sm disabled:opacity-40">›</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LudoAllBetsPage;
